import type { OccupationGroupAlignment } from '../../types/metro';

/**
 * Sort keys exposed by the detail table — one per visible column.
 * Kept in this stand-alone module (not co-located with the
 * component) so the test file can import the sorter without
 * dragging the React component, and so the component file only
 * exports components (react-refresh rule).
 *
 * The 'status' key was dropped 2026-06-05 (Maria) along with the
 * Status column itself -- the ratio's continuous severity pill
 * already encodes the same signal.
 */
export type DetailSortKey =
  | 'group'
  | 'credentials'
  | 'jobs'
  | 'ratio'
  | 'shortage';

export type DetailSortDir = 'asc' | 'desc';

/**
 * Order group rows by the current sort key + direction.
 *
 * Sort key 'group' uses locale-aware string compare; everything
 * else uses numeric compare. `NaN` / `Infinity` ratios sort to the
 * bottom of ascending order so they don't poison the top of the
 * table when source data is dirty.
 */
export function sortGroups(
  groups: ReadonlyArray<OccupationGroupAlignment>,
  key: DetailSortKey | null,
  dir: DetailSortDir,
): ReadonlyArray<OccupationGroupAlignment> {
  if (!key) return groups;
  const direction = dir === 'asc' ? 1 : -1;
  const cloned = [...groups];
  cloned.sort((a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case 'group':
        av = a.name;
        bv = b.name;
        return direction * String(av).localeCompare(String(bv));
      case 'credentials':
        av = a.awardsSb;
        bv = b.awardsSb;
        break;
      case 'jobs':
        av = a.numOpeningsTotal;
        bv = b.numOpeningsTotal;
        break;
      case 'ratio':
        av = Number.isFinite(a.ratio) ? a.ratio : Number.POSITIVE_INFINITY;
        bv = Number.isFinite(b.ratio) ? b.ratio : Number.POSITIVE_INFINITY;
        break;
      case 'shortage':
        av = a.shortage;
        bv = b.shortage;
        break;
    }
    return direction * ((av as number) - (bv as number));
  });
  return cloned;
}
